import { AfterViewInit, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormGroup, Validators, FormBuilder } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../../shared/components';
import * as fromRoot from '../../../../../../../../../state/app.state';
import { ProjectsService } from '../../../../../../../core/projects.service';
import { CommonService } from '../../../../../../../../../shared/services/common.service';
import { AuthService } from '../../../../../../../../../shared/services';
import { SettingsCreateBidItemRequest, SettingsUpdateBidItemRequest, SettingsDeleteBidItemRequest } from '../../../../../../../core/projects.actions';
import { settingsBidSheet } from '../../../../../../../core/projects.selectors';

@Component({
  standalone: false,
  selector: 'app-create-or-edit-bid-item',
  templateUrl: './create-or-edit-bid-item.component.html',
  styleUrls: ['./create-or-edit-bid-item.component.scss'],
})
export class CreateOrEditBidItemComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() data: any;

  form: FormGroup;

  errorMessages: any = {};
  errorMessageMap = {
    item: { required: 'This field is required.' },
    quantity: { required: 'This field is required.' },
    uom: { required: 'This field is required.' },
    unit_price: { required: 'This field is required.' },
    description: { required: 'This field is required.' },
    quantity_rounding: {
      pattern: 'Enter a whole number of decimal places.',
      min: 'Enter a whole number of decimal places.',
      max: 'Decimal places cannot exceed 10.',
    },
  };
  isFormDirty = false;
  projectId: any;
  bidSheetId: string;
  isOrgAdmin = false;
  isProjectManager = false;
  canEditOptions = false;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private store: Store<fromRoot.State>,
    private route: ActivatedRoute,
    private router: Router,
    private projectsService: ProjectsService,
    private commonService: CommonService,
    private authService: AuthService,
    private dialog: MatDialog,
    private fb: FormBuilder
  ) {
    this.form = this.fb.group({
      item: [null, [Validators.required]],
      quantity: [null, [Validators.required]],
      uom: [null, [Validators.required]],
      unit_price: [null, [Validators.required]],
      record_by_station: [false],
      rollup_quantity_editable: [false],
      rollup_formula: [null],
      quantity_rounding: [null, [Validators.pattern(/^\d{1,2}$/), Validators.min(0), Validators.max(10)]],
      description: [null, [Validators.required]],
    });
  }

  ngOnInit(): void {
    if (this.route.snapshot.paramMap.has('projectId')) {
      this.projectId = this.route.snapshot.paramMap.get('projectId');
    } else {
      const url = this.router.url;
      const path = url.split('?')[0];
      const segments = path.split('/');
      this.projectId = segments[segments.length - 1];
    }

    if (!this.projectId && this.data?.projectId) {
      this.projectId = this.data?.projectId;
    }

    const account = this.authService.getCurrentUser();
    const projectRole = account?.roles?.length
      ? account.roles.find((r: any) => r.project_id === this.projectId)?.role ?? account?.role
      : account?.role;
    this.isOrgAdmin = projectRole === 'org-admin';
    this.isProjectManager = projectRole === 'project-manager';
    this.canEditOptions = this.isOrgAdmin || this.isProjectManager;

    if (!this.isOrgAdmin) {
      this.form.get('item')?.disable();
      this.form.get('quantity')?.disable();
      this.form.get('uom')?.disable();
      this.form.get('unit_price')?.disable();
      this.form.get('description')?.disable();
    }
    if (!this.canEditOptions) {
      this.form.get('rollup_quantity_editable')?.disable();
      this.form.get('rollup_formula')?.disable();
      this.form.get('quantity_rounding')?.disable();
    }

    this.patchFormFromData();

    if (!this.isOrgAdmin) {
      this.form.removeControl('record_by_station');
    }

    this.store.select(settingsBidSheet).pipe(takeUntil(this.onDestroy)).subscribe(data => {
      if (data?.id) {
        this.bidSheetId = data.id;
      }
    });

    this.projectsService.isAnyFormDirty.pipe(takeUntil(this.onDestroy)).subscribe(dirty => {
      this.isFormDirty = dirty;
    });

    this.form.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(() => {
      this.isFormDirty = true;
      this.projectsService.isAnyFormDirty.next(true);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && !changes['data'].firstChange) {
      this.patchFormFromData();
    }
  }

  ngAfterViewInit(): void {
    this.projectsService.isAnyFormDirty.next(false);
  }

  submit(): void {
    if (this.form.invalid) {
      this.checkForErrors();
      return;
    }

    const values = this.form.getRawValue() as any;
    const body: any = {
      item: values?.item,
      quantity: values?.quantity,
      uom: values?.uom,
      unit_price: values?.unit_price,
      record_by_station: this.isOrgAdmin
        ? !!values?.record_by_station
        : !!this.data?.record_by_station,
      rollup_quantity_editable: !!values?.rollup_quantity_editable,
      rollup_formula: values?.rollup_formula || null,
      quantity_rounding: this.toRounding(values?.quantity_rounding),
      description: values?.description,
    };

    if (this.data?.id) {
      const bidItemId = this.data.id;
      this.store.dispatch(SettingsUpdateBidItemRequest({ payload: { projectId: this.projectId, bidItemId, body } }));
      return;
    }
    this.store.dispatch(SettingsCreateBidItemRequest({ payload: { projectId: this.projectId, bidSheetId: this.bidSheetId, body } }));
  }

  checkForErrors(currentField?: string): void {
    const validationErrors = this.commonService.checkFormValidation(this.form, this.errorMessageMap, currentField);
    if (currentField && !validationErrors?.[currentField]) {
      // Field is valid again: drop its stale message instead of merging an empty result over it.
      const remaining = { ...this.errorMessages };
      delete remaining[currentField];
      this.errorMessages = remaining;
      return;
    }
    if (validationErrors) {
      this.errorMessages = { ...this.errorMessages, ...validationErrors };
    }
  }

  /** Blank input means "no rounding" (null); otherwise send the decimal-place count as a number. */
  private toRounding(value: unknown): number | null {
    if (value === null || value === undefined || String(value).trim() === '') {
      return null;
    }
    return Number(value);
  }

  deleteBidItem(): void {
    const bidItemLabel =
      this.data?.item && this.data?.description ? `${this.data.item} - ${this.data.description}` : 'this bid item';
    const message = `Deleting <b>${bidItemLabel}</b> cannot be undone.<br />Proceed to delete?`;
    const dialogData = new ConfirmDialogModel('Delete?', message);

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: dialogData,
    });

    dialogRef.afterClosed().pipe(takeUntil(this.onDestroy)).subscribe(result => {
      if (result) {
        this.store.dispatch(SettingsDeleteBidItemRequest({
          payload: {
            projectId: this.projectId,
            bidItemId: this.data?.id,
          },
        }));
      }
    });
  }

  private patchFormFromData(): void {
    if (this.data?.id) {
      this.form.patchValue({
        item: this.data?.item ?? null,
        quantity: this.data?.quantity ?? null,
        uom: this.data?.uom ?? null,
        unit_price: this.data?.unit_price ?? null,
        record_by_station: this.data?.record_by_station ?? false,
        rollup_quantity_editable: this.data?.rollup_quantity_editable ?? false,
        rollup_formula: this.data?.rollup_formula ?? null,
        quantity_rounding: this.data?.quantity_rounding ?? null,
        description: this.data?.description ?? null,
      });
      this.projectsService.isAnyFormDirty.next(false);
      this.isFormDirty = false;
    }
  }

  ngOnDestroy(): void {
    this.projectsService.isAnyFormDirty.next(false);
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
